"use client"

import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import axios from "axios"
import {
  FiDownload,
  FiX,
  FiCheck,
  FiInfo,
  FiTrash2,
  FiFilter,
  FiSearch,
  FiCalendar,
  FiUser,
  FiMail,
  FiPhone,
  FiDollarSign,
  FiSettings,
  FiRefreshCw,
} from "react-icons/fi"

const statusStyles = {
  new: {
    bg: "bg-blue-100",
    text: "text-blue-800",
    border: "border-blue-200",
    dot: "bg-blue-500",
  },
  Processing: {
    bg: "bg-orange-100",
    text: "text-orange-800",
    border: "border-orange-200",
    dot: "bg-orange-500",
  },
  Accepted: {
    bg: "bg-green-100",
    text: "text-green-800",
    border: "border-green-200",
    dot: "bg-green-500",
  },
}

const SolutionsTable = () => {
  const [solutions, setSolutions] = useState([])
  const [filters, setFilters] = useState({
    startup: "",
    founder: "",
    email: "",
    phone: "",
    status: "",
    startDate: "",
    endDate: "",
  })
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [editingStatusId, setEditingStatusId] = useState(null)
  const [newStatus, setNewStatus] = useState("")
  const [infoDialogOpen, setInfoDialogOpen] = useState(false)
  const [infoData, setInfoData] = useState(null)

  useEffect(() => {
    fetchSolutions()
  }, [])

  const fetchSolutions = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/solutions`)
      const sorted = response.data.sort((a, b) => new Date(b.registeredAt) - new Date(a.registeredAt))
      setSolutions(sorted)
    } catch (error) {
      console.error("Error fetching solutions:", error)
    }
  }

  const handleStatusChange = async (id, status) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/solutions/${id}`, { status })
      setSolutions((prev) => prev.map((sol) => (sol._id === id ? { ...sol, status } : sol)))
      setEditingStatusId(null)
    } catch (error) {
      console.error("Error updating status:", error)
    }
  }

  const handleCancel = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/solutions/${selectedItem}`)
      setDeleteDialogOpen(false)
      setSolutions((prev) => prev.filter((sol) => sol._id !== selectedItem))
    } catch (err) {
      console.error("Error cancelling:", err)
    }
  }

  const handleDownloadExcel = async () => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/solutions/export`)
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "solutions.xlsx"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const handleClearFilters = () => {
    setFilters({
      startup: "",
      founder: "",
      email: "",
      phone: "",
      status: "",
      startDate: "",
      endDate: "",
    })
  }

  const filteredSolutions = solutions.filter((item) => {
    const { startup, founder, email, phone, status, startDate, endDate } = filters
    const regDate = new Date(item.registeredAt)
    return (
      (!startup || item.startupName?.toLowerCase().includes(startup.toLowerCase())) &&
      (!founder || item.founderName?.toLowerCase().includes(founder.toLowerCase())) &&
      (!email || item.email?.toLowerCase().includes(email.toLowerCase())) &&
      (!phone || item.phoneNumber?.toLowerCase().includes(phone.toLowerCase())) &&
      (!status || item.status === status) &&
      (!startDate || regDate >= new Date(startDate)) &&
      (!endDate || regDate <= new Date(endDate))
    )
  })

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4 sm:p-6 lg:p-8">
      <div className="w-full mx-auto space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-6"
        >
          <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center">
                <FiSettings className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  🛠 Tech Support
                </h1>
                <p className="text-gray-500 text-sm">Manage technical support solutions</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600 bg-white px-4 py-2 rounded-xl border border-gray-200">
                Total: <span className="font-semibold text-blue-600">{filteredSolutions.length}</span>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDownloadExcel}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2"
              >
                <FiDownload className="text-lg" />
                Download Excel
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <FiFilter className="text-blue-600 text-lg" />
            <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            {/* Text Filters */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FiSearch className="text-xs" />
                Startup
              </label>
              <input
                type="text"
                value={filters.startup}
                onChange={(e) => setFilters({ ...filters, startup: e.target.value })}
                placeholder="Search startup..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FiUser className="text-xs" />
                Founder
              </label>
              <input
                type="text"
                value={filters.founder}
                onChange={(e) => setFilters({ ...filters, founder: e.target.value })}
                placeholder="Search founder..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FiMail className="text-xs" />
                Email
              </label>
              <input
                type="text"
                value={filters.email}
                onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                placeholder="Search email..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FiPhone className="text-xs" />
                Phone
              </label>
              <input
                type="text"
                value={filters.phone}
                onChange={(e) => setFilters({ ...filters, phone: e.target.value })}
                placeholder="Search phone..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              />
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              >
                <option value="">All Status</option>
                <option value="new">New</option>
                <option value="Processing">Processing</option>
                <option value="Accepted">Accepted</option>
              </select>
            </div>

            {/* Date Filters */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FiCalendar className="text-xs" />
                Start Date
              </label>
              <input
                type="datetime-local"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FiCalendar className="text-xs" />
                End Date
              </label>
              <input
                type="datetime-local"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-sm"
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleClearFilters}
              className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-2 rounded-xl font-medium flex items-center gap-2 text-sm"
            >
              <FiRefreshCw className="text-sm" />
              Clear Filters
            </motion.button>
          </div>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl p-6"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Startup
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Founder
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Total Quote
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <AnimatePresence>
                  {filteredSolutions.map((sol, index) => {
                    const totalQuote = sol.services?.reduce((acc, curr) => acc + (curr.quote || 0), 0)
                    const statusStyle = statusStyles[sol.status] || statusStyles.new

                    return (
                      <motion.tr
                        key={sol._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                        className="hover:bg-blue-50/50 transition-colors duration-200"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {new Date(sol.registeredAt).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{sol.startupName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{sol.founderName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{sol.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{sol.phoneNumber}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1 text-sm font-semibold text-gray-900">
                              <FiDollarSign className="text-green-500 text-xs" />₹{totalQuote}
                            </div>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => {
                                setInfoData(sol)
                                setInfoDialogOpen(true)
                              }}
                              className="p-1 hover:bg-blue-100 rounded-full transition-colors duration-200"
                            >
                              <FiInfo className="text-blue-500 text-sm" />
                            </motion.button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingStatusId === sol._id ? (
                            <div className="flex items-center gap-2">
                              <select
                                value={newStatus}
                                onChange={(e) => setNewStatus(e.target.value)}
                                className="px-3 py-1 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                              >
                                {["new", "Processing", "Accepted"].map((status) => (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                ))}
                              </select>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => handleStatusChange(sol._id, newStatus)}
                                className="p-1 text-green-600 hover:bg-green-100 rounded-full transition-colors duration-200"
                              >
                                <FiCheck className="text-sm" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => setEditingStatusId(null)}
                                className="p-1 text-red-600 hover:bg-red-100 rounded-full transition-colors duration-200"
                              >
                                <FiX className="text-sm" />
                              </motion.button>
                            </div>
                          ) : (
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setEditingStatusId(sol._id)
                                setNewStatus(sol.status)
                              }}
                              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border transition-all duration-200 ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}
                            >
                              <div className={`w-2 h-2 rounded-full ${statusStyle.dot}`}></div>
                              {sol.status}
                            </motion.button>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setDeleteDialogOpen(true)
                              setSelectedItem(sol._id)
                            }}
                            className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 mx-auto"
                          >
                            <FiTrash2 className="text-xs" />
                            Cancel
                          </motion.button>
                        </td>
                      </motion.tr>
                    )
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {filteredSolutions.length === 0 && (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <FiSettings className="text-gray-400 text-3xl" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No solutions found</h3>
              <p className="text-gray-500">Try adjusting your filters to see more results</p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Info Dialog */}
      <AnimatePresence>
        {infoDialogOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-2xl max-h-[80vh] overflow-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 p-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                    Quote Details
                  </h2>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setInfoDialogOpen(false)}
                    className="w-10 h-10 bg-gray-100 hover:bg-red-100 rounded-full flex items-center justify-center transition-colors duration-300"
                  >
                    <FiX className="text-gray-600 hover:text-red-600" />
                  </motion.button>
                </div>
              </div>

              <div className="p-6">
                {infoData && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-500">Startup</label>
                        <p className="text-lg font-semibold text-gray-900">{infoData.startupName}</p>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-500">Founder</label>
                        <p className="text-lg font-semibold text-gray-900">{infoData.founderName}</p>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-2xl border border-blue-100">
                      <div className="flex items-center gap-2 mb-2">
                        <FiDollarSign className="text-green-500" />
                        <span className="text-sm font-medium text-gray-700">Total Quote</span>
                      </div>
                      <p className="text-3xl font-bold text-gray-900">
                        ₹{infoData.services?.reduce((acc, curr) => acc + (curr.quote || 0), 0)}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Services Breakdown</h3>
                      <div className="space-y-3">
                        {infoData.services?.map((service, i) => (
                          <div key={i} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-medium text-gray-900">{service.name}</h4>
                              <span className="text-lg font-semibold text-green-600">₹{service.quote}</span>
                            </div>
                            {service.description && <p className="text-sm text-gray-600">{service.description}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Dialog */}
      <AnimatePresence>
        {deleteDialogOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-md"
            >
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <FiTrash2 className="text-red-600 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Confirm Cancellation</h3>
                    <p className="text-sm text-gray-600">This action cannot be undone</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6">
                  Are you sure you want to cancel this solution? This will permanently remove it from the system.
                </p>

                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setDeleteDialogOpen(false)}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleCancel}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl font-medium transition-all duration-300"
                  >
                    Yes, Cancel
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default SolutionsTable